import { Link, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import type { ApiError } from '@/lib/api'
import { AuthLayout } from '@/components/AuthLayout/AuthLayout'
import { ArrowRightIcon } from '@/pages/IndexPage/sorteeLanding/components/icons'
import styles from '@/components/AuthLayout/AuthForm.module.css'

function extractErrorMessage(error: unknown): string {
  const fallback = 'Não foi possível criar sua conta. Tente novamente.'
  if (!error || typeof error !== 'object') return fallback
  const apiError = error as ApiError
  const details = apiError.details as
    | { message?: string; errors?: Record<string, string[]> }
    | undefined

  if (details?.errors) {
    const firstKey = Object.keys(details.errors)[0]
    const firstMessage = firstKey ? details.errors[firstKey]?.[0] : undefined
    if (firstMessage) return firstMessage
  }

  if (details?.message) return details.message
  return apiError.message || fallback
}

export function CadastroPage() {
  const navigate = useNavigate()
  const { register } = useAuth()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (password.length < 8) {
      setErrorMessage('A senha deve ter pelo menos 8 caracteres.')
      return
    }
    if (password !== confirmPassword) {
      setErrorMessage('As senhas não conferem.')
      return
    }

    setErrorMessage(null)
    setIsSubmitting(true)
    try {
      await register({ name, email, password })
      navigate('/home')
    } catch (error) {
      setErrorMessage(extractErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthLayout mode="cadastro">
      <h1 className={styles.title}>Criar conta</h1>
      <p className={styles.subtitle}>
        Seu primeiro passo para criar e gerenciar campanhas.
      </p>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="cadastro-nome">
            Nome completo
          </label>
          <input
            id="cadastro-nome"
            type="text"
            className={styles.input}
            placeholder="Seu nome"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="cadastro-email">
            E-mail
          </label>
          <input
            id="cadastro-email"
            type="email"
            className={styles.input}
            placeholder="seu@email.com"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="cadastro-telefone">
            Telefone <span className={styles.optional}>(opcional)</span>
          </label>
          <input
            id="cadastro-telefone"
            type="tel"
            className={styles.input}
            placeholder="(00) 00000-0000"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="cadastro-senha">
            Senha
          </label>
          <input
            id="cadastro-senha"
            type="password"
            className={styles.input}
            placeholder="Mínimo de 8 caracteres"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="cadastro-confirmar">
            Confirmar senha
          </label>
          <input
            id="cadastro-confirmar"
            type="password"
            className={styles.input}
            placeholder="Repita a senha"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={8}
          />
        </div>

        {errorMessage && (
          <div className={styles.error} role="alert">
            {errorMessage}
          </div>
        )}

        <div className={styles.row}>
          <label className={styles.checkboxWrapper}>
            <input type="checkbox" className={styles.checkbox} name="terms" required />
            <span className={styles.checkboxLabel}>
              Li e aceito os{' '}
              <a
                href="https://sorteios.com.br/termos-de-uso/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Termos de Uso
              </a>{' '}
              e a{' '}
              <a
                href="https://sorteios.com.br/politica-de-privacidade/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Política de Privacidade
              </a>
            </span>
          </label>
        </div>

        <button type="submit" className={styles.submit} disabled={isSubmitting}>
          {isSubmitting ? 'Cadastrando...' : 'Cadastrar'}
          <ArrowRightIcon className={styles.submitIcon} />
        </button>
      </form>

      <div className={styles.footer}>
        <p className={styles.footerText}>
          Já tem uma conta?
          <Link to="/login" className={styles.loginLink}>
            Entrar
          </Link>
        </p>
      </div>
    </AuthLayout>
  )
}
